import React, { useState } from 'react';
import RichTextEditor from '../../../components/RichTextEditor/RichTextEditor';
import WhatsAppTextarea from '../../../components/WhatsAppTextarea/WhatsAppTextarea';
import EmojiTextarea from '../../../components/EmojiPicker/EmojiTextarea';
import EmojiInput from '../../../components/EmojiPicker/EmojiInput';
// LOCAL ONLY — AI Agents integration (Copy Suggester + Compliance Reviewer)
import CopySuggesterModal from '../../../components/AIAgents/CopySuggesterModal';
import VoiceOnboardingModal from '../../../components/AIAgents/VoiceOnboardingModal';
import { getVoiceProfile } from '../../../components/AIAgents/aiAgentsClient';
import { resolveComplianceCategory } from '../../../components/AIAgents/templateCategory';
import ComplianceReviewerModal from '../../../components/AIAgents/ComplianceReviewerModal';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    Text,
    VStack,
    HStack,
    Flex,
    IconButton,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Alert,
    AlertIcon,
    AlertDescription,
    Grid,
    Switch,
    Radio,
    RadioGroup,
    Stack,
    Badge,
    Image,
    Spinner,
    Icon,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Divider,
} from '@chakra-ui/react';

import {
    FiMessageCircle,
    FiMessageSquare,
    FiMail,
    FiBell,
    FiPhone,
    FiSmartphone,
    FiArrowRight,
    FiArrowLeft,
    FiPlus,
    FiHash,
    FiTarget,
    FiTrash2,
    FiCheckCircle,
    FiUpload,
    FiX,
    FiImage,
} from 'react-icons/fi';
import { EmailPreview, InAppPreview, PushPreview, SMSPreview, VoicePreview, WhatsAppPreview } from './ChannelPreviewBox';
import { LuInfo } from 'react-icons/lu';
import { MESSAGE_EXPIRY_OPTIONS } from '../../../utils/DefaultFunction';
import { fetchWrapper } from '../../../helpers';
import { decodeHtmlEntities } from './EmailDesigner/htmlExport';
import { countEmojis } from '../../../utils/countEmojis';



const GSM_7BIT_REGEX =
    /^[\r\n @£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæÉ!"#¤%&'()*+,\-./0-9:;<=>?ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§abcdefghijklmnopqrstuvwxyzäöñüà\^\{\}\\\[~\]|€_]*$/;

const getSmsInfo = (text) => {
    if (!text) return { packetCount: 0, encoding: '7bit', singleLimit: 160, charCount: 0 };
    const isUnicode = !GSM_7BIT_REGEX.test(text);
    if (isUnicode) {
        const len = text.length;
        let packetCount = len <= 70 ? 1 : Math.ceil(len / 67);
        return { packetCount, encoding: 'Unicode', singleLimit: 70, charCount: len };
    } else {
        const EXT_REGEX = /[|\^€{}\[\]~]/g;
        const extCount = (text.match(EXT_REGEX) || []).length;
        const charCount = text.length + extCount;
        let packetCount = charCount <= 160 ? 1 : Math.ceil(charCount / 153);
        return { packetCount, encoding: '7bit', singleLimit: 160, charCount };
    }
};

const validateSignatureHash = (hash) => {
    const regex = /^[a-zA-Z0-9]{11}$/;
    return regex.test(hash) && hash.length === 11;
};

// Flatten a nested JSON object into dot-notation key-value pairs
// { flowId: "X", variables: { amount: 100 } } → [{ key: "flowId", value: "X" }, { key: "variables.amount", value: "100" }]
function flattenObject(obj, prefix = '') {
    const result = [];
    for (const [k, v] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${k}` : k;
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
            result.push(...flattenObject(v, path));
        } else {
            result.push({ key: path, value: v == null ? '' : String(v) });
        }
    }
    return result;
}




const containsVariables = (text) => {
    return /\{\{.*?\}\}/.test(text);
};

const FILE_RULES = {
    image: {
        extensions: ['.jpeg', '.jpg', '.png'],
        mimeTypes: ['image/jpeg', 'image/png'],
        maxBytes: 5 * 1024 * 1024,
        label: 'JPEG, PNG',
        maxLabel: '5 MB',
        accept: 'image/jpeg,image/png',
    },
    video: {
        extensions: ['.mp4', '.3gp'],
        mimeTypes: ['video/mp4', 'video/3gpp', 'video/3gpp2'],
        maxBytes: 16 * 1024 * 1024,
        label: 'MP4, 3GP',
        maxLabel: '16 MB',
        accept: 'video/mp4,video/3gpp,video/3gpp2',
    },
    document: {
        extensions: ['.txt', '.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx', '.pdf'],
        mimeTypes: [
            'text/plain',
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ],
        maxBytes: 100 * 1024 * 1024,
        label: 'PDF, TXT, XLS, XLSX, DOC, DOCX, PPT, PPTX',
        maxLabel: '100 MB',
        accept: '.txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain',
    },
};

// Image Upload Component with better UI
const ImageUploader = ({ label, fileType, onChange, imageUrl, onRemove, uploading, targetId, hint, error }) => {
    const [validationError, setValidationError] = React.useState('');
    const fileInputRef = React.useRef(null);

    const rules = FILE_RULES[fileType] || FILE_RULES.document;

    const handleChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = '.' + file.name.split('.').pop().toLowerCase();
        const isExtOk = rules.extensions.includes(ext);
        const isMimeOk = rules.mimeTypes.includes(file.type);
        const isSizeOk = file.size <= rules.maxBytes;

        if (!isExtOk && !isMimeOk) {
            setValidationError(`Invalid file type. Allowed: ${rules.extensions.join(', ')}`);
            e.target.value = '';
            return;
        }
        if (!isSizeOk) {
            setValidationError(`File too large. Maximum size is ${rules.maxLabel}.`);
            e.target.value = '';
            return;
        }

        setValidationError('');
        onChange(e);
    };

    const displayError = validationError || error;

    return (
        <FormControl>
            <FormLabel fontWeight="semibold">{label}</FormLabel>

            {imageUrl ? (
                <VStack spacing={3} align="stretch">
                    <Box
                        position="relative"
                        borderWidth="2px"
                        borderRadius="lg"
                        borderColor="green.400"
                        overflow="hidden"
                        bg="white"
                        p={2}
                    >
                        {fileType === 'image' ? (
                            <Image
                                src={imageUrl}
                                alt="Uploaded preview"
                                maxH="200px"
                                w="full"
                                objectFit="contain"
                                borderRadius="md"
                            />
                        ) : (
                            <Box textAlign="center" py={4}>
                                <FiCheckCircle size={48} color="green" style={{ margin: '0 auto' }} />
                                <Text fontSize="sm" fontWeight="medium" mt={2} color="green.600">
                                    File Uploaded Successfully
                                </Text>
                            </Box>
                        )}
                        <IconButton
                            aria-label="Remove file"
                            icon={<FiX />}
                            size="sm"
                            bg="white"
                            color="black"
                            position="absolute"
                            top={3}
                            right={3}
                            borderRadius="full"
                            boxShadow="md"
                            onClick={() => { setValidationError(''); onRemove(); }}
                            _hover={{ bg: "none" }}
                        />
                    </Box>

                    <Box bg="gray.50" p={3} borderRadius="md" borderWidth="1px">
                        <HStack spacing={2}>
                            <FiCheckCircle color="green" />
                            <Text fontSize="xs" fontWeight="medium" color="gray.700" noOfLines={1} flex={1}>
                                {imageUrl}
                            </Text>
                        </HStack>
                    </Box>

                    {hint && <Text fontSize="xs" color="gray.600">{hint}</Text>}
                    {displayError && (
                        <Alert status="error" fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>{displayError}</AlertDescription>
                        </Alert>
                    )}
                </VStack>
            ) : (
                <VStack spacing={3} align="stretch">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={rules.accept}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                        id={targetId}
                        disabled={uploading}
                    />

                    <Box
                        as="label"
                        htmlFor={targetId}
                        cursor={uploading ? 'not-allowed' : 'pointer'}
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor={displayError ? 'red.400' : 'gray.300'}
                        borderRadius="lg"
                        p={8}
                        textAlign="center"
                        bg={uploading ? "gray.50" : "white"}
                        transition="all 0.2s"
                        _hover={!uploading ? { borderColor: "blue.400", bg: "blue.50" } : {}}
                    >
                        <VStack spacing={3}>
                            {uploading ? (
                                <>
                                    <Spinner size="xl" color="blue.500" thickness="3px" />
                                    <Text fontWeight="semibold" color="blue.600">Uploading...</Text>
                                    <Text fontSize="xs" color="gray.600">Please wait while we upload your file</Text>
                                </>
                            ) : (
                                <>
                                    <Box color="gray.400">
                                        {fileType === 'image' ? <FiImage size={48} /> : <FiUpload size={48} />}
                                    </Box>
                                    <VStack spacing={1}>
                                        <Text fontWeight="semibold" color="gray.700">
                                            Click to upload or drag and drop
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {rules.label} (Max {rules.maxLabel})
                                        </Text>
                                    </VStack>
                                    <Button
                                        size="sm"
                                        colorScheme="blue"
                                        variant="solid"
                                        leftIcon={<FiUpload />}
                                        pointerEvents="none"
                                    >
                                        Choose File
                                    </Button>
                                </>
                            )}
                        </VStack>
                    </Box>

                    {hint && <Text fontSize="xs" color="gray.600">{hint}</Text>}
                    {displayError && (
                        <Alert status="error" fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>{displayError}</AlertDescription>
                        </Alert>
                    )}
                </VStack>
            )}
        </FormControl>
    );
};



export function ChannelTabsFixed(props) {
    const cardBg = 'white';
    const borderColor = 'gray.200';
    const bgMuted = 'gray.50';

    // LOCAL ONLY — AI Agents modal state for SMS / WhatsApp / Email / Push body editors.
    // Each channel has its own copy-suggester + compliance-check button so the
    // operator can ask for variants or run a safe-message-filter pass before
    // saving the template.
    const [aiCopyOpen, setAiCopyOpen] = useState({ channel: null, text: '', setter: null });
    const [aiComplianceOpen, setAiComplianceOpen] = useState({ channel: null, text: '' });
    const openCopyFor = (channel, text, setter) => setAiCopyOpen({ channel, text, setter });
    const openComplianceFor = (channel, text) => setAiComplianceOpen({ channel, text });

    // ─── Voice Profile state (FEAT-0008) ───────────────────────────────
    // Drives the AI button surface: when no profile exists for this
    // tenant the button reads "Setup Brand Voice for AI" and opens the
    // VoiceOnboardingModal. Once a profile is saved, the button flips
    // to "Generate Templates (AI)" and opens the CopySuggesterModal as
    // usual. Tenant-scoped (one profile per customer_id); profile
    // presence is decided once per page load.
    const [voiceProfile, setVoiceProfile] = React.useState(null);
    const [voiceLoaded,  setVoiceLoaded]  = React.useState(false);
    const [voiceOnboardingOpen, setVoiceOnboardingOpen] = React.useState(false);
    // After-onboarding intent: when the operator clicks "Setup Brand
    // Voice" with an inline brief, we remember which channel + body
    // they intended to generate for so we can open the generator
    // automatically the moment the profile is saved.
    const [pendingGenerate, setPendingGenerate] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await getVoiceProfile();
                if (cancelled) return;
                setVoiceProfile(r?.profile || null);
            } catch {
                // Agents console may be offline; treat as no profile
                // (button still shows "Setup Brand Voice for AI" and
                // surfaces the unreachable error inside the modal).
                if (!cancelled) setVoiceProfile(null);
            } finally {
                if (!cancelled) setVoiceLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const hasVoiceProfile = !!voiceProfile;

    /**
     * Click handler for the AI button on any channel tab.
     * If no profile exists → open onboarding (and remember the
     * channel/body/setter to auto-resume on save). If a profile
     * exists → open the generator as usual.
     */
    const openAiCopyFor = (channel, text, setter) => {
        if (!hasVoiceProfile) {
            setPendingGenerate({ channel, text, setter });
            setVoiceOnboardingOpen(true);
        } else {
            openCopyFor(channel, text, setter);
        }
    };



    const hanldeOtpTypeChange = (value) => {
        props.setOtpType(value);
    };

    const getExpiryOptionsForTemplate = (templateType) => {
        return MESSAGE_EXPIRY_OPTIONS[templateType] || MESSAGE_EXPIRY_OPTIONS['1'];
    };

    const expiryOptions = getExpiryOptionsForTemplate(props.templateType);
    const isDocumentSelected = props.whatsappHeader.type === 'document'

    const isFullHtmlDoc = (html) => {
        const t = (html || '').trim().toLowerCase();
        return t.startsWith('<!doctype') || t.startsWith('<html');
    };
    const initialCodeMode = props.emailType === 'html_code' ||
        (props.emailType === 'html' && isFullHtmlDoc(props.emailHtml));

    const [payloadJson, setPayloadJson] = React.useState('');
    const [payloadError, setPayloadError] = React.useState('');

    const importPayload = (jsonText) => {
        try {
            const parsed = JSON.parse(jsonText);
            props.setIvrVariables(flattenObject(parsed).map(p => p.key));
            setPayloadError('');
        } catch {
            setPayloadError('Invalid JSON — please check your payload format.');
        }
    };

    // Pre-fill payloadJson textarea when editing a saved template
    React.useEffect(() => {
        const vars = (props.ivrVariables || []);
        if (vars.length === 0 || payloadJson) return;
        const obj = {};
        for (const v of vars) {
            const key = typeof v === 'object' ? (v.key || '') : (v || '');
            if (!key) continue;
            const parts = key.split('.');
            let cur = obj;
            for (let i = 0; i < parts.length - 1; i++) {
                if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
                cur = cur[parts[i]];
            }
            cur[parts[parts.length - 1]] = '';
        }
        setPayloadJson(JSON.stringify(obj, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ivrVariables]);

    const initialDesignerMode = props.emailType === 'email_template';
    const [designerMode, setDesignerMode] = React.useState(initialDesignerMode);
    const [designerEmails, setDesignerEmails] = React.useState([]);
    const [designerEmailsLoading, setDesignerEmailsLoading] = React.useState(false);
    const [selectedDesignerEmailId, setSelectedDesignerEmailId] = React.useState(props.emailTemplateId || '');
    const [designerEmailLoading, setDesignerEmailLoading] = React.useState(false);

    const loadDesignerEmails = React.useCallback(async () => {
        setDesignerEmailsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/email-template/list?page=1&limit=100&is_template=false`;
            const res = await fetchWrapper.get(url);
            const list = (res?.data || []).filter(item => !item.is_template);
            setDesignerEmails(list);
        } catch (error) {
            console.error('Failed to load Email Designer emails:', error);
        } finally {
            setDesignerEmailsLoading(false);
        }
    }, []);

    // Load the picker list up front when editing a template that already uses this mode
    React.useEffect(() => {
        if (initialDesignerMode) loadDesignerEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectDesignerEmail = async (id) => {
        setSelectedDesignerEmailId(id);
        if (!id) return;
        setDesignerEmailLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/email-template/get-by-id?id=${id}`;
            const res = await fetchWrapper.get(url);
            const html = decodeHtmlEntities(res?.data?.html_code || '');
            setEmailCodeInput(html);
            props.setEmailCode(html);
            // Keep emailHtml populated too so the existing variable-extraction /
            // cross-channel consistency checks (which scan emailHtml) work for this mode.
            props.setEmailHtml(html);
            props.setEmailTemplateId?.(id);
        } catch (error) {
            console.error('Failed to load selected email:', error);
        } finally {
            setDesignerEmailLoading(false);
        }
    };

    const [emailCodeMode, setEmailCodeMode] = React.useState(initialCodeMode || initialDesignerMode);
    const [emailCodeInput, setEmailCodeInput] = React.useState((initialCodeMode || initialDesignerMode) ? (props.emailCode || props.emailHtml || '') : '');
    const [localHtmlContent, setLocalHtmlContent] = React.useState(
        props.emailType === 'html' && !initialCodeMode ? (props.emailHtml || '') : ''
    );
    const [localPlainText, setLocalPlainText] = React.useState(
        props.emailType === 'text' ? (props.emailHtml || '') : ''
    );

    // Sync local state when parent loads template data asynchronously
    React.useEffect(() => {
        const isCodeType = props.emailType === 'html_code' ||
            (props.emailType === 'html' && isFullHtmlDoc(props.emailHtml));
        if (props.emailType === 'email_template') {
            setDesignerMode(true);
            setEmailCodeMode(true);
            setEmailCodeInput(props.emailHtml || '');
            setSelectedDesignerEmailId(props.emailTemplateId || '');
            if (designerEmails.length === 0) loadDesignerEmails();
        } else if (isCodeType) {
            setDesignerMode(false);
            setEmailCodeMode(true);
            setEmailCodeInput(props.emailCode || props.emailHtml || '');
        } else if (props.emailType === 'html') {
            setDesignerMode(false);
            setEmailCodeMode(false);
            setLocalHtmlContent(props.emailHtml || '');
        } else if (props.emailType === 'text') {
            setDesignerMode(false);
            setEmailCodeMode(false);
            setLocalPlainText(props.emailHtml || '');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.emailType, props.emailCode, props.emailHtml, props.emailTemplateId]);
    const channelPanels = {
        'Whatsapp': () => (
            <VStack spacing={4} align="stretch">
                {/* Enable/Disable Toggle */}
                <Flex
                    p={4}
                    bg={bgMuted}
                    borderRadius="lg"
                    justify="space-between"
                    align="center"
                >
                    <HStack spacing={3}>
                        <FiMessageCircle size={20} color="green" />
                        <Box>
                            <Text fontWeight="medium">WhatsApp Channel</Text>
                            <Text fontSize="sm" color="gray.600">
                                Rich messaging with media and buttons
                            </Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-whatsapp"
                            isChecked={props.selectedChannels.includes('Whatsapp')}
                            onChange={() => props.handleChannelToggle('Whatsapp')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-whatsapp"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('Whatsapp') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('Whatsapp') && (
                    <>
                        {/* Sync Button */}
                        {props.selectedChannels.includes('In-App Notification') && (
                            <Flex justify="flex-end">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<FiArrowRight />}
                                    onClick={props.syncWhatsAppToInApp}
                                >
                                    Sync to Inbox
                                </Button>
                            </Flex>
                        )}

                        {/* Settings and Preview Grid */}
                        <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                            {/* Left Panel - Settings */}
                            <Box>
                                <Box className='box-style-card'>
                                    <Box>
                                        {props.isTemplateApproved && (
                                            <Alert bg={"red.50"} border={"1px"} borderStyle={"solid"} borderColor={"red.200"} borderRadius={"8.75px"} p={4} my={1}>
                                                <LuInfo color='#e7000b' style={{ marginRight: "1rem" }} />
                                                Your WhatsApp template has been approved. Whatsapp message content can no longer be modified.
                                            </Alert>
                                        )}
                                        <VStack spacing={6} align="stretch">
                                            {/* Header Section */}
                                            <Box>
                                                <VStack spacing={4} align="stretch">
                                                    {/* <Box display={`${isDocumentSelected ? "flex" : "block"}`}> */}
                                                    <Text fontWeight="medium" >Header (Optional)</Text>
                                                        {props.templateType !== '1' && (
                                                            <Box display={`${isDocumentSelected ? "flex" : "block"}`} gap={4}>
                                                                
                                                                <FormControl>
                                                                    <FormLabel>Header Type</FormLabel>
                                                                    <Select
                                                                        disabled={props.isTemplateApproved}
                                                                        value={props.whatsappHeader.type || 'none'}
                                                                        onChange={(e) => props.handleWhatsAppHeaderTypeChange(e.target.value === 'none' ? null : e.target.value)}
                                                                    >
                                                                        <option value="none">None</option>
                                                                        <option value="text">Text</option>
                                                                        <option value="image">Image</option>
                                                                        <option value="video">Video</option>
                                                                        <option value="document">Document</option>
                                                                        <option value="location">Location</option>
                                                                    </Select>
                                                                </FormControl>
                                                                {props.whatsappHeader.type === 'document' && (
                                                                    <FormControl isRequired>
                                                                        <FormLabel>Document Filename</FormLabel>
                                                                        <Input
                                                                            value={props.whatsappHeader.document_filename || ''}
                                                                            onChange={(e) => props.setWhatsappHeader(prev => ({ ...prev, document_filename: e.target.value }))}
                                                                            placeholder="Enter document filename (e.g., invoice.pdf)"
                                                                            maxLength={100}
                                                                            disabled={props.isTemplateApproved}
                                                                        />
                                                                        <Flex justify="flex-end" mt={1}>
                                                                            {props.getCharacterCount(props.whatsappHeader.document_filename || '', 100)}
                                                                        </Flex>
                                                                        <Text fontSize="xs" color="gray.600" >
                                                                            This filename will be displayed to recipients
                                                                        </Text>
                                                                    </FormControl>
                                                                )}
                                                            </Box>
                                                        )}



                                                    {/* </Box> */}
                                                    {(props.whatsappHeader.type === 'text' && props.templateType !== '1') && (
                                                        <FormControl>
                                                            <FormLabel isRequired>Header Text</FormLabel>
                                                            <Input
                                                                value={props.whatsappHeader.content}
                                                                onChange={(e) => props.setWhatsappHeader(prev => ({ ...prev, content: e.target.value }))}
                                                                placeholder="Enter header text"
                                                                maxLength={60}
                                                                disabled={props.isTemplateApproved}
                                                            />
                                                            <Flex justify="flex-end" mt={1}>
                                                                {props.getCharacterCount(props.whatsappHeader.content, 60)}
                                                            </Flex>

                                                            {containsVariables(props.whatsappHeader.content) && (
                                                                <Alert status="error" mt={2}>
                                                                    <AlertIcon />
                                                                    <AlertDescription fontSize="sm">
                                                                        Variables are not allowed in header
                                                                    </AlertDescription>
                                                                </Alert>
                                                            )}
                                                        </FormControl>
                                                    )}



                                                    {(props.whatsappHeader.type === 'image' ||
                                                        props.whatsappHeader.type === 'video' ||
                                                        props.whatsappHeader.type === 'document') && (
                                                            <ImageUploader
                                                                label={`${props.whatsappHeader.type.charAt(0).toUpperCase() + props.whatsappHeader.type.slice(1)} Header`}
                                                                fileType={props.whatsappHeader.type}
                                                                onChange={(e) => props.handleFileUpload(e, 'whatsapp-header')}
                                                                imageUrl={props.whatsappHeader.file}
                                                                onRemove={() => props.removeImage('whatsapp-header')}
                                                                uploading={props.uploading}
                                                                targetId="whatsapp-header-file"
                                                                hint="This will appear at the top of your WhatsApp message"
                                                            />
                                                        )}

                                                    {props.whatsappHeader.type === 'location' && (
                                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                                            <FormControl isRequired>
                                                                <FormLabel>Location Name</FormLabel>
                                                                <Input
                                                                    placeholder="Enter location name"
                                                                    disabled={props.isTemplateApproved}
                                                                    value={props.whatsappHeader.location_name || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        location_name: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                            <FormControl isRequired>
                                                                <FormLabel>Address</FormLabel>
                                                                <Input
                                                                    placeholder="Enter address"
                                                                    disabled={props.isTemplateApproved}
                                                                    value={props.whatsappHeader.location_address || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        location_address: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                            <FormControl isRequired>
                                                                <FormLabel>Latitude</FormLabel>
                                                                <Input
                                                                    placeholder="Enter latitude"
                                                                    disabled={props.isTemplateApproved}
                                                                    type="number"
                                                                    step="any"
                                                                    value={props.whatsappHeader.latitude || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        latitude: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                            <FormControl isRequired>
                                                                <FormLabel>Longitude</FormLabel>
                                                                <Input
                                                                    placeholder="Enter longitude"
                                                                    disabled={props.isTemplateApproved}
                                                                    type="number"
                                                                    step="any"
                                                                    value={props.whatsappHeader.longitude || ''}
                                                                    onChange={(e) => props.setWhatsappHeader({
                                                                        ...props.whatsappHeader,
                                                                        longitude: e.target.value
                                                                    })}
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    )}
                                                </VStack>
                                            </Box>

                                            {/* Body Section */}
                                            <FormControl isRequired>
                                                <Flex align="center" mb={2} justify="space-between">
                                                    <FormLabel mb={0}>Body</FormLabel>
                                                    {/* LOCAL ONLY — AI Agents: Copy Suggester + Compliance Reviewer */}
                                                    <HStack spacing={2}>
                                                        <Button
                                                            size="xs"
                                                            type="button"
                                                            leftIcon={<span>{hasVoiceProfile ? "✨" : "🪄"}</span>}
                                                            bgGradient={hasVoiceProfile
                                                                ? "linear(to-r, purple.500, blue.500)"
                                                                : "linear(to-r, purple.500, pink.500)"}
                                                            color="white"
                                                            _hover={{ bgGradient: hasVoiceProfile
                                                                ? "linear(to-r, purple.600, blue.600)"
                                                                : "linear(to-r, purple.600, pink.600)" }}
                                                            onClick={() => openAiCopyFor("whatsapp", props.whatsappBody, props.setWhatsappBody)}
                                                            isDisabled={props.isTemplateApproved || !voiceLoaded}
                                                        >
                                                            {hasVoiceProfile ? "Generate Templates (AI)" : "Setup Brand Voice for AI"}
                                                        </Button>
                                                        <Button
                                                            size="xs"
                                                            type="button"
                                                            variant="outline"
                                                            colorScheme="orange"
                                                            leftIcon={<span>🛡️</span>}
                                                            onClick={() => openComplianceFor("whatsapp", props.whatsappBody)}
                                                            isDisabled={!props.whatsappBody}
                                                        >
                                                            Compliance Check
                                                        </Button>
                                                    </HStack>
                                                </Flex>
                                                {/* Authentication (templateType '1') is locked to Meta's approved copy —
                                                    whether or not "Add security recommendation" is on, which is why the
                                                    toggle is no longer part of this condition. Every OTHER channel gets
                                                    the same prefill but stays editable. */}
                                                <WhatsAppTextarea
                                                    value={props.whatsappBody}
                                                    onChange={(e) => props.setWhatsappBody(e.target.value)}
                                                    placeholder="Enter message body"
                                                    rows={4}
                                                    isReadOnly={props.templateType === '1'}
                                                    bg={props.templateType === '1' ? bgMuted : 'white'}
                                                    cursor={props.templateType === '1' ? 'not-allowed' : 'text'}
                                                    disabled={props.isTemplateApproved}
                                                    isInvalid={props.whatsappBody.length > 1024 || countEmojis(props.whatsappBody) > 10}
                                                />
                                                {props.templateType === '1' && (
                                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                                        Fixed by WhatsApp for authentication templates. Toggle “Add security
                                                        recommendation” to change the wording.
                                                    </Text>
                                                )}
                                                <Flex justify="space-between" mt={1}>
                                                    <Text fontSize="xs" color={countEmojis(props.whatsappBody) > 10 ? 'red.500' : 'gray.500'}>
                                                        {countEmojis(props.whatsappBody)}/10 emojis {countEmojis(props.whatsappBody) > 10 && '(limit exceeded)'}
                                                    </Text>
                                                    {props.getCharacterCount(props.whatsappBody, 1024)}
                                                </Flex>
                                                {(props.whatsappBody.length > 1024 || countEmojis(props.whatsappBody) > 10) && (
                                                    <Box mt={2} bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" px={3} py={2.5}>
                                                        <VStack align="start" spacing={1.5}>
                                                            {props.whatsappBody.length > 1024 && (
                                                                <HStack spacing={2}>
                                                                    <Box w="5px" h="5px" borderRadius="full" bg="red.400" flexShrink={0} />
                                                                    <Text fontSize="xs" color="red.700">
                                                                        <Text as="span" fontWeight="700">{(props.whatsappBody.length - 1024).toLocaleString()}</Text> character{props.whatsappBody.length - 1024 !== 1 ? 's' : ''} over the 1,024 limit
                                                                    </Text>
                                                                </HStack>
                                                            )}
                                                            {countEmojis(props.whatsappBody) > 10 && (
                                                                <HStack spacing={2}>
                                                                    <Box w="5px" h="5px" borderRadius="full" bg="red.400" flexShrink={0} />
                                                                    <Text fontSize="xs" color="red.700">
                                                                        <Text as="span" fontWeight="700">{countEmojis(props.whatsappBody) - 10}</Text> emoji{countEmojis(props.whatsappBody) - 10 !== 1 ? 's' : ''} over the limit of 10
                                                                    </Text>
                                                                </HStack>
                                                            )}
                                                        </VStack>
                                                    </Box>
                                                )}
                                            </FormControl>

                                            {/* Footer Section */}
                                            {props.templateType !== '1' && (
                                                <FormControl>
                                                    <FormLabel>Footer (Optional)</FormLabel>
                                                    <Input
                                                        value={props.whatsappFooter}
                                                        onChange={(e) => props.setWhatsappFooter(e.target.value)}
                                                        placeholder="Enter footer text"
                                                        maxLength={60}
                                                        disabled={props.isTemplateApproved}
                                                    />
                                                    <Flex justify="flex-end" mt={1}>
                                                        {props.getCharacterCount(props.whatsappFooter, 60)}
                                                    </Flex>
                                                    {containsVariables(props.whatsappFooter) && (
                                                        <Alert status="error" mt={2}>
                                                            <AlertIcon />
                                                            <AlertDescription fontSize="sm">
                                                                Variables are not allowed in footer
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </FormControl>
                                            )}

                                            {/* Buttons or OTP Configuration */}
                                            {props.templateType === '1' ? (
                                                <VStack spacing={4} align="stretch">
                                                    <FormControl>
                                                        <FormLabel>OTP Authentication Type</FormLabel>
                                                        <Text fontSize="xs" color="gray.600" mb={2}>
                                                            Choose how customers send the code from WhatsApp to your app.
                                                        </Text>
                                                        <Select
                                                            value={props.otpType}
                                                            onChange={(e) => hanldeOtpTypeChange(e.target.value)}
                                                            placeholder="Select authentication type"
                                                            disabled={props.isTemplateApproved}
                                                        >
                                                            <option value="COPY_CODE">Copy Code</option>
                                                            <option value="ONE_TAP">One-tap Autofill</option>
                                                            <option value="ZERO_TAP">Zero-tap Autofill</option>
                                                        </Select>
                                                    </FormControl>

                                                    {(props.otpType === 'ONE_TAP' || props.otpType === 'ZERO_TAP') && (
                                                        <Box p={4} borderWidth="1px" borderRadius="md" bg={bgMuted}>
                                                            <VStack spacing={4} align="stretch">
                                                                <Box>
                                                                    <Text fontWeight="medium" mb={2}>App Setup</Text>
                                                                    <Text fontSize="sm" color="gray.600">You can add up to 5 apps.</Text>
                                                                </Box>

                                                                {props.otpApps.map((app, index) => (
                                                                    <Box key={index} borderTopWidth={index > 0 ? '1px' : 0} pt={4}>
                                                                        <Flex justify="space-between" mb={3}>
                                                                            <Text fontSize="sm" fontWeight="medium">App {index + 1}</Text>
                                                                            {props.otpApps.length > 1 && (
                                                                                <IconButton
                                                                                    aria-label="Remove app"
                                                                                    icon={<FiTrash2 />}
                                                                                    size="sm"
                                                                                    colorScheme="red"
                                                                                    variant="ghost"
                                                                                    onClick={() => props.removeOtpApp(index)}
                                                                                />
                                                                            )}
                                                                        </Flex>

                                                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                                                            <FormControl isRequired>
                                                                                <FormLabel fontSize="sm">Package Name</FormLabel>
                                                                                <Input
                                                                                    value={app.package_name}
                                                                                    onChange={(e) => props.updateOtpApp(index, 'package_name', e.target.value)}
                                                                                    placeholder="com.example.app"
                                                                                    maxLength={224}
                                                                                    size="sm"
                                                                                    disabled={props.isTemplateApproved}
                                                                                />
                                                                                <Flex justify="flex-end" mt={1}>
                                                                                    <Text fontSize="xs" color="gray.600">
                                                                                        {app.package_name.length}/224
                                                                                    </Text>
                                                                                </Flex>
                                                                            </FormControl>

                                                                            <FormControl isRequired>
                                                                                <FormLabel fontSize="sm">App Signature Hash</FormLabel>
                                                                                <Input
                                                                                    value={app.signature_hash}
                                                                                    onChange={(e) => props.updateOtpApp(index, 'signature_hash', e.target.value)}
                                                                                    placeholder="xXxXxXxXxXx"
                                                                                    maxLength={11}
                                                                                    size="sm"
                                                                                    isInvalid={app.signature_hash && !validateSignatureHash(app.signature_hash)}
                                                                                    disabled={props.isTemplateApproved}
                                                                                />
                                                                                <Flex justify="flex-end" mt={1}>
                                                                                    <Text fontSize="xs" color={app.signature_hash && !validateSignatureHash(app.signature_hash) ? "red.500" : "gray.600"}>
                                                                                        {app.signature_hash.length}/11
                                                                                    </Text>
                                                                                </Flex>
                                                                                {app.signature_hash && !validateSignatureHash(app.signature_hash) && (
                                                                                    <Text fontSize="xs" color="red.500" mt={1}>
                                                                                        Must be exactly 11 alphanumeric characters (a-z, A-Z, 0-9)
                                                                                    </Text>
                                                                                )}
                                                                            </FormControl>
                                                                        </Grid>
                                                                    </Box>
                                                                ))}

                                                                {props.otpApps.length < 5 && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        leftIcon={<FiPlus />}
                                                                        onClick={props.addOtpApp}
                                                                        w="full"
                                                                    >
                                                                        Add another app
                                                                    </Button>
                                                                )}
                                                            </VStack>
                                                        </Box>
                                                    )}
                                                </VStack>
                                            ) : (
                                                <Box>
                                                    <Flex justify="space-between" align="center" mb={3}>
                                                        <Text fontWeight="medium">Call-to-Action Buttons (Optional)</Text>
                                                        {props.whatsappButtons.length < 3 && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<FiPlus />}
                                                                onClick={props.addWhatsAppButton}
                                                                disabled={props.isTemplateApproved}
                                                            >
                                                                Add Button
                                                            </Button>
                                                        )}
                                                    </Flex>
                                                    {props.renderButtonConfiguration(
                                                        props.whatsappButtons,
                                                        props.updateWhatsAppButton,
                                                        props.removeWhatsAppButton,
                                                        3,
                                                        'Button',
                                                        true
                                                    )}
                                                </Box>
                                            )}

                                            {props.templateType === '1' && (
                                                <Box p={4} borderWidth="1px" borderRadius="md" bg={bgMuted}>
                                                    <VStack spacing={4} align="stretch">
                                                        <Box>
                                                            <Text fontWeight="medium" mb={2}>Content Options</Text>
                                                            <Text fontSize="sm" color="gray.600" mb={3}>
                                                                Content for authentication message templates can't be edited. You can add additional content from the options below.
                                                            </Text>
                                                        </Box>

                                                        <Checkbox
                                                            isChecked={props.authAddSecurityRecommendation}
                                                            onChange={(e) => props.setAuthAddSecurityRecommendation(e.target.checked)}
                                                            disabled={props.isTemplateApproved}
                                                        >
                                                            Add security recommendation
                                                        </Checkbox>

                                                        <Checkbox
                                                            isChecked={props.authAddExpirationTime}
                                                            onChange={(e) => props.setAuthAddExpirationTime(e.target.checked)}
                                                            disabled={props.isTemplateApproved}
                                                        >
                                                            Add expiration time for the code
                                                        </Checkbox>
                                                        {props.authAddExpirationTime && (
                                                            <FormControl>
                                                                <FormLabel fontSize="sm">Code Expiration Time (minutes)</FormLabel>
                                                                <NumberInput
                                                                    value={props.authCodeExpiryMinutes ?? ''}
                                                                    min={1}
                                                                    max={90}
                                                                    step={1}
                                                                    clampValueOnBlur
                                                                    keepWithinRange
                                                                    disabled={props.isTemplateApproved}
                                                                    onChange={(valueString, valueNumber) => {
                                                                        if (valueString === '') {
                                                                            props.setAuthCodeExpiryMinutes('');
                                                                            return;
                                                                        }
                                                                        if (!isNaN(valueNumber)) {
                                                                            props.setAuthCodeExpiryMinutes(valueNumber);
                                                                        }
                                                                    }}
                                                                    size="sm"
                                                                >
                                                                    <NumberInputField placeholder="Enter minutes (1–90)" />
                                                                    <NumberInputStepper>
                                                                        <NumberIncrementStepper />
                                                                        <NumberDecrementStepper />
                                                                    </NumberInputStepper>
                                                                </NumberInput>
                                                                <Text fontSize="xs" color="gray.600" mt={1}>
                                                                    Set how long the authentication code remains valid (1-90 minutes)
                                                                </Text>
                                                            </FormControl>
                                                        )}
                                                    </VStack>
                                                </Box>
                                            )}

                                            {/* Message Expiry */}
                                            <Box>
                                                <Flex justify="space-between" align="center" mb={2}>
                                                    <FormLabel mb={0}>Message Expiry Time</FormLabel>
                                                    <Switch
                                                        isChecked={props.whatsappMessageExpiryEnabled}
                                                        onChange={(e) => props.setWhatsappMessageExpiryEnabled(e.target.checked)}
                                                        disabled={props.isTemplateApproved}
                                                    />
                                                </Flex>
                                                <Text fontSize="xs" color="gray.600" mb={2}>
                                                    Set a custom validity period for message delivery.
                                                </Text>
                                                {props.whatsappMessageExpiryEnabled && (
                                                    <FormControl style={{ width: '200px' }}>
                                                        <Select
                                                            value={props.whatsappMessageExpiry}
                                                            onChange={(e) => props.setWhatsappMessageExpiry(e.target.value)}
                                                            disabled={props.isTemplateApproved}
                                                        >
                                                            {expiryOptions.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </Box>
                                        </VStack>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Right Panel - Preview */}
                            <Box>
                                <Box position="sticky" top={4}>
                                    <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>
                                        Preview
                                    </Text>
                                    <WhatsAppPreview
                                        header={props.whatsappHeader}
                                        body={props.whatsappBody}
                                        footer={props.whatsappFooter}
                                        buttons={props.whatsappButtons}
                                        otpType={props.otpType}
                                        authAddExpirationTime={props.authAddExpirationTime}
                                        authCodeExpiryMinutes={props.authCodeExpiryMinutes}
                                        templateType={props.templateType}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </>
                )}
            </VStack>
        ),
        'SMS': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <FiMessageSquare size={20} color="blue" />
                        <Box>
                            <Text fontWeight="medium">SMS Channel</Text>
                            <Text fontSize="sm" color="gray.600">Simple text messages</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-SMS"
                            isChecked={props.selectedChannels.includes('SMS')}
                            onChange={() => props.handleChannelToggle('SMS')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-SMS"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('SMS') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('SMS') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <Box>
                                <FormControl isRequired>
                                    <Flex align="center" mb={2} justify="space-between">
                                        <FormLabel mb={0}>SMS Message</FormLabel>
                                        {/* LOCAL ONLY — AI Agents: Copy Suggester + Compliance Reviewer */}
                                        <HStack spacing={2}>
                                            <Button
                                                size="xs"
                                                type="button"
                                                leftIcon={<span>{hasVoiceProfile ? "✨" : "🪄"}</span>}
                                                bgGradient={hasVoiceProfile
                                                    ? "linear(to-r, purple.500, blue.500)"
                                                    : "linear(to-r, purple.500, pink.500)"}
                                                color="white"
                                                _hover={{ bgGradient: hasVoiceProfile
                                                    ? "linear(to-r, purple.600, blue.600)"
                                                    : "linear(to-r, purple.600, pink.600)" }}
                                                onClick={() => openAiCopyFor("sms", props.smsBody, props.setSmsBody)}
                                                isDisabled={!voiceLoaded}
                                            >
                                                {hasVoiceProfile ? "Generate Templates (AI)" : "Setup Brand Voice for AI"}
                                            </Button>
                                            <Button
                                                size="xs"
                                                type="button"
                                                variant="outline"
                                                colorScheme="orange"
                                                leftIcon={<span>🛡️</span>}
                                                onClick={() => openComplianceFor("sms", props.smsBody)}
                                                isDisabled={!props.smsBody}
                                            >
                                                Compliance Check
                                            </Button>
                                        </HStack>
                                    </Flex>
                                    <Textarea
                                        value={props.smsBody}
                                        onChange={(e) => props.setSmsBody(e.target.value)}
                                        placeholder="Enter SMS message content"
                                        rows={4}
                                    />

                                    {(() => {
                                        const { packetCount, encoding, singleLimit, charCount } = getSmsInfo(props.smsBody);
                                        const isMulti = packetCount > 1;
                                        const isUnicode = encoding === 'Unicode';
                                        return (
                                            <>
                                                <Flex justify="space-between" align="center" mt={2}>
                                                    <HStack spacing={2}>
                                                        <Text fontSize="xs" color={isMulti ? 'orange.600' : 'gray.600'}>
                                                            {charCount} characters
                                                        </Text>
                                                        {isMulti && (
                                                            <>
                                                                <Text color="gray.400">•</Text>
                                                                <Badge colorScheme="orange">
                                                                    <HStack spacing={1}>
                                                                        <FiHash size={12} />
                                                                        <Text fontSize="xs">{packetCount} SMS Messages</Text>
                                                                    </HStack>
                                                                </Badge>
                                                            </>
                                                        )}
                                                    </HStack>
                                                </Flex>

                                                {props.smsBody.length > 0 && (
                                                    <Box
                                                        mt={2} px={3} py={2} borderRadius="md"
                                                        bg={isUnicode ? 'purple.50' : 'blue.50'}
                                                        border="1px solid"
                                                        borderColor={isUnicode ? 'purple.200' : 'blue.200'}
                                                    >
                                                        <HStack spacing={2}>
                                                            <Badge
                                                                colorScheme={isUnicode ? 'purple' : 'blue'}
                                                                fontSize="10px"
                                                                borderRadius="sm"
                                                            >
                                                                {encoding}
                                                            </Badge>
                                                            <Text fontSize="xs" color={isUnicode ? 'purple.700' : 'blue.700'}>
                                                                {isUnicode
                                                                    ? `Your SMS is Unicode — limit is 70 chars (1 SMS) / 67 chars per part (multi)`
                                                                    : `Your SMS is 7-bit — limit is 160 chars (1 SMS) / 153 chars per part (multi)`}
                                                            </Text>
                                                        </HStack>
                                                    </Box>
                                                )}

                                                {isMulti && (
                                                    <Alert status="info" mt={2}>
                                                        <AlertIcon />
                                                        <AlertDescription fontSize="sm">
                                                            <Text fontWeight="medium">Multi-part SMS detected</Text>
                                                            <Text>
                                                                This {encoding} message will be sent as {packetCount} separate SMS messages ({isUnicode ? '67' : '153'} chars/part).
                                                            </Text>
                                                        </AlertDescription>
                                                    </Alert>
                                                )}

                                                {!isMulti && props.smsBody.length > 0 && (
                                                    <HStack spacing={1} mt={2}>
                                                        <FiTarget size={12} color="green" />
                                                        <Text fontSize="xs" color="green.600">
                                                            Single SMS — fits within {singleLimit} character limit ({encoding})
                                                        </Text>
                                                    </HStack>
                                                )}
                                            </>
                                        );
                                    })()}
                                </FormControl>

                            </Box>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <SMSPreview body={props.smsBody} />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
        'Push Notification': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <FiBell size={20} color="orange" />
                        <Box>
                            <Text fontWeight="medium">Push Notification Channel</Text>
                            <Text fontSize="sm" color="gray.600">Mobile app notifications</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-push"
                            isChecked={props.selectedChannels.includes('Push Notification')}
                            onChange={() => props.handleChannelToggle('Push Notification')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-push"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('Push Notification') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('Push Notification') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <Box>
                                <VStack spacing={4} align="stretch">
                                    <FormControl isRequired>
                                        <FormLabel>Notification Title</FormLabel>
                                        <EmojiInput
                                            value={props.pushTitle}
                                            onChange={(e) => props.setPushTitle(e.target.value)}
                                            placeholder="Enter notification title"
                                            maxLength={50}
                                        />
                                        <Flex justify="flex-end" mt={1}>
                                            {props.getCharacterCount(props.pushTitle, 50)}
                                        </Flex>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel>Notification Body</FormLabel>
                                        <EmojiTextarea
                                            value={props.pushBody}
                                            onChange={(e) => props.setPushBody(e.target.value)}
                                            placeholder="Enter notification body"
                                            rows={3}
                                            maxLength={150}
                                        />
                                        <Flex justify="flex-end" mt={1}>
                                            {props.getCharacterCount(props.pushBody, 150)}
                                        </Flex>
                                    </FormControl>
                                    {props.templateType !== "1" && (
                                        <FormControl>
                                            <FormLabel>Deep Link (Optional)</FormLabel>
                                            <Input
                                                value={props.deeplink || ''}
                                                onChange={(e) => props.setPushDeepLink(e.target.value)}
                                                placeholder="e.g., myapp://screen/details?id=$orderId"
                                            />
                                            <Text fontSize="xs" color="gray.600" mt={1}>
                                                Deep link to navigate users to a specific screen in your app. Use $variable_name for dynamic values — enter their sample values in the Variables section below.
                                            </Text>
                                        </FormControl>
                                    )}

                                    <ImageUploader
                                        label="Push Notification Image (Optional)"
                                        fileType="image"
                                        onChange={(e) => props.handleFileUpload(e, 'push-image')}
                                        imageUrl={props.pushImage}
                                        onRemove={() => props.removeImage('push-image')}
                                        uploading={props.uploading}
                                        targetId="push-image-file"
                                        hint="Add an eye-catching image to your push notification"
                                    />
                                </VStack>
                            </Box>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <PushPreview title={props.pushTitle} body={props.pushBody} imageUrl={props.pushImage} />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
        'In-App Notification': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <FiSmartphone size={20} color="cyan" />
                        <Box>
                            <Text fontWeight="medium">Inbox Channel</Text>
                            <Text fontSize="sm" color="gray.600">Inbox messages and alerts</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-in-app"
                            isChecked={props.selectedChannels.includes('In-App Notification')}
                            onChange={() => props.handleChannelToggle('In-App Notification')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-in-app"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('In-App Notification') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('In-App Notification') && (
                    <>
                        {props.selectedChannels.includes('Whatsapp') && (
                            <Flex justify="flex-end">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<FiArrowLeft />}
                                    onClick={props.syncInAppToWhatsApp}
                                >
                                    Sync to WhatsApp
                                </Button>
                            </Flex>
                        )}

                        <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                            <Box>
                                <Box className='box-style-card'>
                                    <Box>
                                        <VStack spacing={1} align="stretch">
                                            <VStack spacing={4} align="stretch">
                                                {props.templateType !== "1" && (
                                                    <>

                                                        <Text fontWeight="medium" mb={1}>Header (Optional)</Text>
                                                        <FormControl>
                                                            <FormLabel>Header Type</FormLabel>
                                                            <Select
                                                                value={props.inAppHeader.type || 'none'}
                                                                onChange={(e) => props.handleInAppHeaderTypeChange(e.target.value === 'none' ? "none" : e.target.value)}
                                                            >
                                                                <option value="none">None</option>
                                                                <option value="text">Text</option>
                                                                <option value="image">Image</option>
                                                                <option value="video">Video</option>
                                                                <option value="document">Document</option>
                                                            </Select>
                                                        </FormControl>
                                                    </>
                                                )}

                                                {props.inAppHeader.type === 'text' && (
                                                    props.templateType !== "1" && (
                                                        <FormControl isRequired>
                                                            <FormLabel>Header Text</FormLabel>
                                                            <EmojiInput
                                                                value={props.inAppHeader.content}
                                                                onChange={(e) => props.setInAppHeader(prev => ({ ...prev, content: e.target.value }))}
                                                                placeholder="Enter header text"
                                                                maxLength={60}
                                                            />
                                                            <Flex justify="flex-end" mt={1}>
                                                                {props.getCharacterCount(props.inAppHeader.content, 60)}
                                                            </Flex>
                                                            {containsVariables(props.inAppHeader.content) && (
                                                                <Alert status="error" mt={2}>
                                                                    <AlertIcon />
                                                                    <AlertDescription fontSize="sm">
                                                                        Variables are not allowed in header
                                                                    </AlertDescription>
                                                                </Alert>
                                                            )}
                                                        </FormControl>
                                                    )
                                                )}

                                                {(props.inAppHeader.type === 'image' ||
                                                    props.inAppHeader.type === 'video' ||
                                                    props.inAppHeader.type === 'document') && (
                                                        <ImageUploader
                                                            label={`${props.inAppHeader.type.charAt(0).toUpperCase() + props.inAppHeader.type.slice(1)} Header`}
                                                            fileType={props.inAppHeader.type}
                                                            onChange={(e) => props.handleFileUpload(e, 'inapp-header')}
                                                            imageUrl={props.inAppHeader.file}
                                                            onRemove={() => props.removeImage('inapp-header')}
                                                            uploading={props.uploading}
                                                            targetId="inapp-header-file"
                                                            hint="This will appear at the top of your Inbox notification"
                                                        />
                                                    )}
                                            </VStack>

                                            <FormControl>
                                                <FormLabel>Body</FormLabel>
                                                <EmojiTextarea
                                                    value={props.inAppBody}
                                                    onChange={(e) => props.setInAppBody(e.target.value)}
                                                    placeholder="Enter message body"
                                                    rows={4}
                                                    maxLength={1024}
                                                />
                                                <Flex justify="flex-end" mt={1}>
                                                    {props.getCharacterCount(props.inAppBody, 1024)}
                                                </Flex>
                                            </FormControl>

                                            {props.templateType !== "1" && (
                                                <FormControl>
                                                    <FormLabel>Footer (Optional)</FormLabel>
                                                    <EmojiInput
                                                        value={props.inAppFooter}
                                                        onChange={(e) => props.setInAppFooter(e.target.value)}
                                                        placeholder="Enter footer text"
                                                        maxLength={60}
                                                    />
                                                    <Flex justify="flex-end" mt={1}>
                                                        {props.getCharacterCount(props.inAppFooter, 60)}
                                                    </Flex>
                                                    {containsVariables(props.inAppFooter) && (
                                                        <Alert status="error" mt={2}>
                                                            <AlertIcon />
                                                            <AlertDescription fontSize="sm">
                                                                Variables are not allowed in footer
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </FormControl>
                                            )}

                                            {props.templateType !== "1" && (
                                                <Box>
                                                    <Flex justify="space-between" align="center" mb={3}>
                                                        <Text fontWeight="medium">Call-to-Action Buttons (Optional)</Text>
                                                        {props.inAppButtons.length < 3 && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<FiPlus />}
                                                                onClick={props.addInAppButton}
                                                            >
                                                                Add Button
                                                            </Button>
                                                        )}
                                                    </Flex>
                                                    {props.renderButtonConfiguration(
                                                        props.inAppButtons,
                                                        props.updateInAppButton,
                                                        props.removeInAppButton,
                                                        3,
                                                        'Button',
                                                        false,
                                                        true
                                                    )}
                                                </Box>
                                            )}
                                        </VStack>
                                    </Box>
                                </Box>
                            </Box>

                            <Box>
                                <Box position="sticky" top={4}>
                                    <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                    <InAppPreview
                                        header={props.inAppHeader}
                                        body={props.inAppBody}
                                        footer={props.inAppFooter}
                                        buttons={props.inAppButtons}
                                        otpType={props.otpType}
                                        isWhatsapp={props.selectedChannels.includes('Whatsapp') ? true : false}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </>
                )}
            </VStack>
        ),
        'Voice (IVR)': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <FiPhone size={20} color="purple" />
                        <Box>
                            <Text fontWeight="medium">Voice Call Channel</Text>
                            <Text fontSize="sm" color="gray.600">Automated voice calls</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-voice"
                            isChecked={props.selectedChannels.includes('Voice (IVR)')}
                            onChange={() => props.handleChannelToggle('Voice (IVR)')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-voice"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('Voice (IVR)') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('Voice (IVR)') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <VStack spacing={5} align="stretch">

                                {/* ── Voice Call Type ── */}
                                <Box>
                                    <FormLabel fontWeight="semibold" mb={3}>Voice Call Type</FormLabel>
                                    <RadioGroup
                                        value={props.voiceCallType || 'text_to_voice'}
                                        onChange={props.setVoiceCallType}
                                    >
                                        <VStack spacing={3} align="stretch">
                                            <Box
                                                as="label"
                                                htmlFor="voice-type-text"
                                                p={3}
                                                border="1px solid"
                                                borderColor={(props.voiceCallType || 'text_to_voice') === 'text_to_voice' ? 'purple.300' : 'gray.200'}
                                                borderRadius="lg"
                                                cursor="pointer"
                                                bg={(props.voiceCallType || 'text_to_voice') === 'text_to_voice' ? 'purple.50' : 'white'}
                                                _hover={{ bg: 'purple.50' }}
                                                transition="all 0.15s"
                                            >
                                                <HStack spacing={3}>
                                                    <Radio id="voice-type-text" value="text_to_voice" colorScheme="purple" />
                                                    <HStack spacing={2}>
                                                        <FiPhone size={16} color="#9810fa" />
                                                        <Box>
                                                            <Text fontWeight="medium" fontSize="sm">Text to Voice</Text>
                                                            <Text fontSize="xs" color="gray.500">Convert text script to automated voice call</Text>
                                                        </Box>
                                                    </HStack>
                                                </HStack>
                                            </Box>

                                            <Box
                                                as="label"
                                                htmlFor="voice-type-recorded"
                                                p={3}
                                                border="1px solid"
                                                borderColor={props.voiceCallType === 'recorded_call' ? 'purple.300' : 'gray.200'}
                                                borderRadius="lg"
                                                cursor="pointer"
                                                bg={props.voiceCallType === 'recorded_call' ? 'purple.50' : 'white'}
                                                _hover={{ bg: 'purple.50' }}
                                                transition="all 0.15s"
                                            >
                                                <HStack spacing={3}>
                                                    <Radio id="voice-type-recorded" value="recorded_call" colorScheme="purple" />
                                                    <HStack spacing={2}>
                                                        <FiPhone size={16} color="#9810fa" />
                                                        <Box>
                                                            <Text fontWeight="medium" fontSize="sm">Recorded Call</Text>
                                                            <Text fontSize="xs" color="gray.500">Use pre-recorded audio file for voice calls</Text>
                                                        </Box>
                                                    </HStack>
                                                </HStack>
                                            </Box>
                                        </VStack>
                                    </RadioGroup>
                                </Box>

                                <Divider />

                                {/* ── Text to Voice ── */}
                                {(props.voiceCallType || 'text_to_voice') === 'text_to_voice' && (
                                    <>
                                        <FormControl isRequired>
                                            <FormLabel>Voice Script</FormLabel>
                                            <Textarea
                                                value={props.voiceScript}
                                                onChange={(e) => props.setVoiceScript(e.target.value)}
                                                placeholder="Enter voice call script or generate from master content"
                                                rows={4}
                                                maxLength={300}
                                            />
                                            <Flex justify="space-between" align="center" mt={1}>
                                                <Text fontSize="xs" color={props.voiceScript.length > 300 ? 'red.500' : 'gray.500'}>
                                                    {props.voiceScript.length}/300 characters
                                                </Text>
                                            </Flex>
                                            {props.voiceScript.length > 300 && (
                                                <Alert status="warning" mt={2} borderRadius="md">
                                                    <AlertIcon />
                                                    <AlertDescription fontSize="sm">
                                                        Voice script exceeds recommended 300 character limit. Longer scripts may be truncated or cause delivery issues.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Retry Attempts</FormLabel>
                                            <Select
                                                value={props.voiceRetries || ''}
                                                onChange={(e) => props.setVoiceRetries(e.target.value)}
                                                placeholder="Select retry attempts"
                                            >
                                                <option value="1">1 attempt</option>
                                                <option value="2">2 attempts</option>
                                                <option value="3">3 attempts</option>
                                                <option value="5">5 attempts</option>
                                            </Select>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Number of retry attempts if the call fails or goes unanswered
                                            </Text>
                                        </FormControl>
                                    </>
                                )}

                                {/* ── Recorded Call ── */}
                                {props.voiceCallType === 'recorded_call' && (
                                    <>
                                        <Box>
                                            <Flex justify="space-between" align="center" mb={3}>
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="semibold">Fixed Recorded Call IDs</Text>
                                                    <Text fontSize="xs" color="gray.500">Add key-value pairs for your recorded call</Text>
                                                </Box>
                                                <Button
                                                    leftIcon={<FiPlus />}
                                                    size="sm"
                                                    variant="outline"
                                                    colorScheme="purple"
                                                    onClick={() => props.setVoiceCallIds([...(props.voiceCallIds || []), { key: '', value: '' }])}
                                                >
                                                    Add
                                                </Button>
                                            </Flex>

                                            {(props.voiceCallIds || []).length === 0 ? (
                                                <Flex
                                                    direction="column" align="center" justify="center"
                                                    py={8} border="1px solid" borderColor="gray.200"
                                                    borderRadius="lg" bg="gray.50" color="gray.400"
                                                >
                                                    <FiPhone size={28} style={{ marginBottom: '8px', opacity: 0.3 }} />
                                                    <Text fontSize="sm">No call IDs yet</Text>
                                                    <Text fontSize="xs" mt={1}>Click Add to define a key-value pair</Text>
                                                </Flex>
                                            ) : (
                                                <VStack spacing={2} align="stretch">
                                                    <Grid templateColumns="1fr 1fr 32px" gap={2} px={1}>
                                                        <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">Key Name</Text>
                                                        <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">Call ID Value</Text>
                                                        <Box />
                                                    </Grid>
                                                    {(props.voiceCallIds || []).map((pair, idx) => (
                                                        <Grid
                                                          key={idx}
                                                          templateColumns="1fr 1fr 32px"
                                                          gap={2}
                                                          alignItems="center"
                                                          p={2}
                                                          border="1px solid"
                                                          borderColor="purple.100"
                                                          borderRadius="lg"
                                                          bg="purple.50"
                                                        >
                                                          <Input
                                                            value={pair.key || ''}
                                                            onChange={(e) => {
                                                              const u = [...(props.voiceCallIds || [])];
                                                              u[idx] = { ...u[idx], key: e.target.value };
                                                              props.setVoiceCallIds(u);
                                                            }}
                                                            placeholder="e.g., callId"
                                                            size="sm" bg="white" fontFamily="mono"
                                                          />
                                                          <Input
                                                            value={pair.value || ''}
                                                            onChange={(e) => {
                                                              const u = [...(props.voiceCallIds || [])];
                                                              u[idx] = { ...u[idx], value: e.target.value };
                                                              props.setVoiceCallIds(u);
                                                            }}
                                                            placeholder="Enter call ID value"
                                                            size="sm" bg="white"
                                                          />
                                                          <IconButton
                                                            icon={<FiTrash2 />}
                                                            variant="ghost"
                                                            colorScheme="red"
                                                            size="sm"
                                                            onClick={() =>
                                                              props.setVoiceCallIds(
                                                                (props.voiceCallIds || []).filter((_, i) => i !== idx)
                                                              )
                                                            }
                                                            aria-label="Remove"
                                                          />
                                                        </Grid>
                                                    ))}
                                                </VStack>
                                            )}
                                        </Box>

                                        <Divider />

                                        {/* Variable Source */}
                                        <Box>
                                            <FormLabel fontWeight="semibold" mb={3}>Required Variables</FormLabel>
                                            <RadioGroup
                                                value={props.voiceVariableSource || 'from_channels'}
                                                onChange={props.setVoiceVariableSource}
                                            >
                                                <VStack spacing={3} align="stretch" mb={4}>
                                                    <Box
                                                        as="label"
                                                        htmlFor="var-source-channels"
                                                        p={3}
                                                        border="1px solid"
                                                        borderColor={(props.voiceVariableSource || 'from_channels') === 'from_channels' ? 'purple.300' : 'gray.200'}
                                                        borderRadius="lg"
                                                        cursor="pointer"
                                                        bg={(props.voiceVariableSource || 'from_channels') === 'from_channels' ? 'purple.50' : 'white'}
                                                        _hover={{ bg: 'gray.50' }}
                                                        transition="all 0.15s"
                                                    >
                                                        <HStack spacing={3}>
                                                            <Radio id="var-source-channels" value="from_channels" colorScheme="purple" />
                                                            <HStack spacing={2}>
                                                                <FiHash size={16} color="#9810fa" />
                                                                <Box>
                                                                    <Text fontWeight="medium" fontSize="sm">Use Variables from Other Channels</Text>
                                                                    <Text fontSize="xs" color="gray.500">Use variables already defined in WhatsApp, SMS, or other channels</Text>
                                                                </Box>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>

                                                    <Box
                                                        as="label"
                                                        htmlFor="var-source-custom"
                                                        p={3}
                                                        border="1px solid"
                                                        borderColor={props.voiceVariableSource === 'custom' ? 'purple.300' : 'gray.200'}
                                                        borderRadius="lg"
                                                        cursor="pointer"
                                                        bg={props.voiceVariableSource === 'custom' ? 'purple.50' : 'white'}
                                                        _hover={{ bg: 'gray.50' }}
                                                        transition="all 0.15s"
                                                    >
                                                        <HStack spacing={3}>
                                                            <Radio id="var-source-custom" value="custom" colorScheme="purple" />
                                                            <HStack spacing={2}>
                                                                <FiPlus size={16} color="#9810fa" />
                                                                <Box>
                                                                    <Text fontWeight="medium" fontSize="sm">Define Custom Variables</Text>
                                                                    <Text fontSize="xs" color="gray.500">Add specific variables for this voice call only</Text>
                                                                </Box>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                </VStack>
                                            </RadioGroup>

                                            {/* From Channels — show existing channel variables */}
                                            {(props.voiceVariableSource || 'from_channels') === 'from_channels' && (
                                                (props.allChannelVariables || []).length === 0 ? (
                                                    <Flex
                                                        direction="column"
                                                        align="center"
                                                        justify="center"
                                                        py={8}
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        borderRadius="lg"
                                                        bg="gray.50"
                                                        color="gray.400"
                                                    >
                                                        <FiHash size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                                                        <Text fontSize="sm">No variables found in other channels</Text>
                                                        <Text fontSize="xs" mt={1}>Add variables to WhatsApp, SMS, or other channel content first</Text>
                                                    </Flex>
                                                ) : (
                                                    <VStack spacing={3} align="stretch">
                                                        <Alert status="info" borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.200">
                                                            <AlertIcon color="blue.500" />
                                                            <AlertDescription fontSize="xs" color="blue.800">
                                                                The following variables are detected from other channels and will be available for this voice call.
                                                            </AlertDescription>
                                                        </Alert>
                                                        <Box p={4} border="1px solid" borderColor="purple.200" borderRadius="lg" bg="purple.50">
                                                            <Flex flexWrap="wrap" gap={2}>
                                                                {(props.allChannelVariables || []).map((variable, index) => (
                                                                    <Box
                                                                        key={index}
                                                                        px={3}
                                                                        py={1.5}
                                                                        bg="white"
                                                                        border="1px solid"
                                                                        borderColor="purple.300"
                                                                        borderRadius="md"
                                                                        fontSize="sm"
                                                                        fontFamily="mono"
                                                                    >
                                                                        ${variable}
                                                                    </Box>
                                                                ))}
                                                            </Flex>
                                                        </Box>
                                                    </VStack>
                                                )
                                            )}

                                            {/* Custom Variables — payload key-value editor */}
                                            {props.voiceVariableSource === 'custom' && (
                                                <VStack spacing={4} align="stretch">

                                                    {/* ── Payload JSON drop zone ── */}
                                                    <Box p={4} border="1px solid" borderColor="gray.200" borderRadius="lg" bg="gray.50">
                                                        <VStack spacing={3} align="stretch">
                                                            <Flex justify="space-between" align="center">
                                                                <Box>
                                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">Payload JSON</Text>
                                                                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                                                                        Paste your payload — variable names are auto-extracted
                                                                    </Text>
                                                                </Box>
                                                                <Button
                                                                    size="xs" colorScheme="purple" variant="outline"
                                                                    onClick={() => importPayload(payloadJson)}
                                                                    isDisabled={!payloadJson.trim()}
                                                                >
                                                                    Import
                                                                </Button>
                                                            </Flex>

                                                            <Textarea
                                                                value={payloadJson}
                                                                onChange={(e) => {
                                                                    setPayloadJson(e.target.value);
                                                                    setPayloadError('');
                                                                    if (e.target.value.trim()) {
                                                                        try { props.setIvrVariables(flattenObject(JSON.parse(e.target.value)).map(p => p.key)); }
                                                                        catch (_) { /* wait for complete valid JSON */ }
                                                                    }
                                                                }}
                                                                placeholder={'{\n  "flowId": "POS_DEBIT",\n  "variables": {\n    "scheme": "VISA",\n    "amount": 2500,\n    "merchantName": "KFC"\n  }\n}'}
                                                                rows={7}
                                                                fontFamily="mono"
                                                                fontSize="sm"
                                                                bg="white"
                                                                resize="vertical"
                                                            />

                                                            {payloadError && (
                                                                <Alert status="error" borderRadius="md" py={2}>
                                                                    <AlertIcon boxSize={3} />
                                                                    <AlertDescription fontSize="xs">{payloadError}</AlertDescription>
                                                                </Alert>
                                                            )}

                                                            <Text fontSize="xs" color="gray.400">
                                                                Top-level key →{" "}
                                                                <Text as="span" fontFamily="mono" color="purple.600">flowId</Text>
                                                                {'  ·  '}
                                                                Nested key →{" "}
                                                                <Text as="span" fontFamily="mono" color="purple.600">variables.amount</Text>
                                                            </Text>
                                                        </VStack>
                                                    </Box>

                                                    {/* ── Variable Names ── */}
                                                    <Box>
                                                        <Flex justify="space-between" align="center" mb={3}>
                                                            <Box>
                                                                <Text fontSize="sm" fontWeight="semibold" color="gray.700">Variables</Text>
                                                                <Text fontSize="xs" color="gray.500">
                                                                    Variable names auto-populated from payload; add more manually
                                                                </Text>
                                                            </Box>
                                                            <Button
                                                                leftIcon={<FiPlus />}
                                                                size="sm"
                                                                variant="outline"
                                                                colorScheme="purple"
                                                                onClick={() => props.setIvrVariables([...(props.ivrVariables || []), ''])}
                                                            >
                                                                Add
                                                            </Button>
                                                        </Flex>

                                                        {(props.ivrVariables || []).length === 0 ? (
                                                            <Flex
                                                                direction="column" align="center" justify="center"
                                                                py={8} border="1px solid" borderColor="gray.200"
                                                                borderRadius="lg" bg="gray.50" color="gray.400"
                                                            >
                                                                <FiHash size={28} style={{ marginBottom: '8px', opacity: 0.3 }} />
                                                                <Text fontSize="sm">No variables yet</Text>
                                                                <Text fontSize="xs" mt={1}>Paste a JSON payload above or add manually</Text>
                                                            </Flex>
                                                        ) : (
                                                            <VStack spacing={2} align="stretch">
                                                                {/* Column header */}
                                                                <Grid templateColumns="1fr 32px" gap={2} px={1}>
                                                                    <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                                                                        Name
                                                                    </Text>
                                                                    <Box />
                                                                </Grid>

                                                                {(props.ivrVariables || []).map((name, idx) => (
                                                                    <Grid
                                                                      key={idx}
                                                                      templateColumns="1fr 32px"
                                                                      gap={2}
                                                                      alignItems="center"
                                                                      p={2}
                                                                      border="1px solid"
                                                                      borderColor="purple.100"
                                                                      borderRadius="lg"
                                                                      bg="purple.50"
                                                                    >
                                                                      <Input
                                                                        value={typeof name === 'object' ? (name.key || '') : (name || '')}
                                                                        onChange={(e) => {
                                                                          const u = [...(props.ivrVariables || [])];
                                                                          u[idx] = e.target.value;
                                                                          props.setIvrVariables(u);
                                                                        }}
                                                                        placeholder="e.g., flowId or variables.amount"
                                                                        size="sm" bg="white" fontFamily="mono"
                                                                      />
                                                                      <IconButton
                                                                        icon={<FiTrash2 />}
                                                                        variant="ghost"
                                                                        colorScheme="red"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                          props.setIvrVariables(
                                                                            (props.ivrVariables || []).filter((_, i) => i !== idx)
                                                                          )
                                                                        }
                                                                        aria-label="Remove"
                                                                      />
                                                                    </Grid>
                                                                ))}
                                                            </VStack>
                                                        )}
                                                    </Box>

                                                    {(props.ivrVariables || []).some(p => typeof p === 'object' ? p.key : p) && (
                                                        <Alert status="info" borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.200">
                                                            <AlertIcon color="blue.500" />
                                                            <AlertDescription fontSize="xs" color="blue.800">
                                                                These variable names will be used when the voice call is triggered.
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </VStack>
                                            )}
                                        </Box>

                                        <FormControl>
                                            <FormLabel>Retry Attempts</FormLabel>
                                            <Select
                                                value={props.voiceRetries || ''}
                                                onChange={(e) => props.setVoiceRetries(e.target.value)}
                                                placeholder="Select retry attempts"
                                            >
                                                <option value="1">1 attempt</option>
                                                <option value="2">2 attempts</option>
                                                <option value="3">3 attempts</option>
                                                <option value="5">5 attempts</option>
                                            </Select>
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                                Number of retry attempts if the call fails or goes unanswered
                                            </Text>
                                        </FormControl>
                                    </>
                                )}
                            </VStack>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <VoicePreview
                                    script={
                                        props.voiceCallType === 'recorded_call'
                                            ? `[Recorded Call]\nCall ID: ${props.voiceCallId || '—'}`
                                            : props.voiceScript
                                    }
                                />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
        'E-Mail': () => (
            <VStack spacing={4} align="stretch">
                <Flex p={4} bg={bgMuted} borderRadius="lg" justify="space-between" align="center">
                    <HStack spacing={3}>
                        <FiMail size={20} color="red" />
                        <Box>
                            <Text fontWeight="medium">Email Channel</Text>
                            <Text fontSize="sm" color="gray.600">HTML emails with rich content</Text>
                        </Box>
                    </HStack>

                    <HStack spacing={2}>
                        <Switch
                            id="enable-mail"
                            isChecked={props.selectedChannels.includes('E-Mail')}
                            onChange={() => props.handleChannelToggle('E-Mail')}
                            colorScheme="green"
                        />
                        <FormLabel
                            htmlFor="enable-mail"
                            mb={0}
                            cursor="pointer"
                            fontWeight="medium"
                        >
                            {props.selectedChannels.includes('E-Mail') ? 'Enabled' : 'Disabled'}
                        </FormLabel>
                    </HStack>
                </Flex>

                {props.selectedChannels.includes('E-Mail') && (
                    <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={6}>
                        <Box className='box-style-card'>
                            <Box>
                                <VStack spacing={4} align="stretch">
                                    <FormControl isRequired>
                                        <FormLabel>Email Subject</FormLabel>
                                        <Input
                                            value={props.emailSubject}
                                            onChange={(e) => props.setEmailSubject(e.target.value)}
                                            placeholder="Enter email subject"
                                            maxLength={100}
                                        />
                                        <Flex justify="flex-end" mt={1}>
                                            {props.getCharacterCount(props.emailSubject, 100)}
                                        </Flex>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel>Content Type</FormLabel>
                                        <RadioGroup
                                            value={designerMode ? 'designer' : emailCodeMode ? 'code' : props.emailType}
                                            onChange={(val) => {
                                                if (val === 'designer') {
                                                    setDesignerMode(true);
                                                    setEmailCodeMode(true);
                                                    props.setEmailType('email_template');
                                                    props.setEmailHtml('');
                                                    loadDesignerEmails();
                                                } else if (val === 'code') {
                                                    setDesignerMode(false);
                                                    setEmailCodeMode(true);
                                                    props.setEmailType('html_code');
                                                    props.setEmailHtml('');
                                                    props.setEmailTemplateId?.('');
                                                    props.setEmailCode(emailCodeInput);
                                                } else {
                                                    setDesignerMode(false);
                                                    setEmailCodeMode(false);
                                                    props.setEmailType(val);
                                                    props.setEmailTemplateId?.('');
                                                    if (val === 'html') {
                                                        props.setEmailHtml(localHtmlContent);
                                                    }
                                                    if (val === 'text') {
                                                        props.setEmailHtml(localPlainText);
                                                    }
                                                }
                                            }}
                                        >
                                            <Stack direction="column" spacing={2}>
                                                <Radio value="text">Plain Text</Radio>
                                                <Radio value="html">Rich Text</Radio>
                                                <Radio value="code">HTML Code</Radio>
                                                <Radio value="designer">Email Templates</Radio>
                                            </Stack>
                                        </RadioGroup>
                                    </FormControl>

                                    {designerMode && (
                                        <FormControl isRequired>
                                            <FormLabel>Select Email Template</FormLabel>
                                            <Select
                                                placeholder={designerEmailsLoading ? 'Loading email templates...' : 'Choose an email template'}
                                                value={selectedDesignerEmailId}
                                                isDisabled={designerEmailsLoading || designerEmailLoading}
                                                onChange={(e) => handleSelectDesignerEmail(e.target.value)}
                                            >
                                                {designerEmails.map(item => (
                                                    <option key={item.id} value={item.id}>{item.name}</option>
                                                ))}
                                            </Select>
                                            {designerEmailLoading && (
                                                <Flex align="center" gap={2} mt={2}>
                                                    <Spinner size="xs" />
                                                    <Text fontSize="xs" color="gray.500">Loading email content...</Text>
                                                </Flex>
                                            )}
                                            {!designerEmailsLoading && designerEmails.length === 0 && (
                                                <Text fontSize="xs" color="gray.500" mt={2}>
                                                    No email templates found. Create one in the Email Designer tool first.
                                                </Text>
                                            )}
                                            {selectedDesignerEmailId && !designerEmailLoading && (
                                                <Text fontSize="xs" color="gray.500" mt={2}>
                                                    Content is fixed to the selected template's design. See preview on the right.
                                                </Text>
                                            )}
                                        </FormControl>
                                    )}

                                    {designerMode ? null : emailCodeMode ? (
                                        <FormControl isRequired isInvalid={(emailCodeInput?.length || 0) > 65000}>
                                            <FormLabel>HTML Code</FormLabel>
                                            <textarea
                                                value={emailCodeInput}
                                                onChange={(e) => {
                                                    setEmailCodeInput(e.target.value);
                                                    props.setEmailCode(e.target.value);
                                                }}
                                                placeholder="Paste your full HTML email code here..."
                                                style={{
                                                    width: '100%',
                                                    minHeight: '320px',
                                                    fontFamily: '"Courier New", Courier, monospace',
                                                    fontSize: '12px',
                                                    padding: '12px',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '6px',
                                                    resize: 'vertical',
                                                    outline: 'none',
                                                    lineHeight: '1.5',
                                                    backgroundColor: '#f8fafc',
                                                    color: '#1a202c',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                            <Flex justify="flex-end" mt={1}>
                                                <Text fontSize="xs" color={(emailCodeInput?.length || 0) > 65000 ? 'red.500' : (emailCodeInput?.length || 0) > 58500 ? 'orange.500' : 'gray.600'}>
                                                    {(emailCodeInput?.length || 0).toLocaleString()} / 65,000
                                                </Text>
                                            </Flex>
                                            {(emailCodeInput?.length || 0) > 65000 && (
                                                <Alert status="error" mt={2}>
                                                    <AlertIcon />
                                                    <AlertDescription fontSize="sm">
                                                        Email content exceeds the 65,000 character limit.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </FormControl>
                                    ) : (
                                        <FormControl isRequired isInvalid={(props.emailHtml?.length || 0) > 65000}>
                                            <FormLabel>
                                                {props.emailType === 'html' ? 'HTML Content' : 'Plain Text Content'}
                                            </FormLabel>
                                            {props.emailType === 'html' ? (
                                                <>
                                                    <Box
                                                        border={(props.emailHtml?.length || 0) > 65000 ? '2px solid' : undefined}
                                                        borderColor="red.400"
                                                        borderRadius="md"
                                                    >
                                                        <RichTextEditor
                                                            value={localHtmlContent}
                                                            onChange={(val) => {
                                                                setLocalHtmlContent(val);
                                                                props.setEmailHtml(val);
                                                            }}
                                                            placeholder="Write HTML email content here..."
                                                        />
                                                    </Box>
                                                    <Flex justify="flex-end" mt={1}>
                                                        <Text
                                                            fontSize="xs"
                                                            color={
                                                                (props.emailHtml?.length || 0) > 65000
                                                                    ? 'red.500'
                                                                    : (props.emailHtml?.length || 0) > 58500
                                                                    ? 'orange.500'
                                                                    : 'gray.600'
                                                            }
                                                        >
                                                            {(props.emailHtml?.length || 0).toLocaleString()} / 65,000
                                                        </Text>
                                                    </Flex>
                                                    {(props.emailHtml?.length || 0) > 65000 && (
                                                        <Alert status="error" mt={2}>
                                                            <AlertIcon />
                                                            <AlertDescription fontSize="sm">
                                                                Email content exceeds the 65,000 character limit. Please reduce the content before saving.
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <Textarea
                                                        value={localPlainText}
                                                        onChange={(e) => {
                                                            setLocalPlainText(e.target.value);
                                                            props.setEmailHtml(e.target.value);
                                                        }}
                                                        placeholder="Enter plain text content"
                                                        rows={8}
                                                        maxLength={65000}
                                                    />
                                                    <Flex justify="flex-end" mt={1}>
                                                        {props.getCharacterCount(props.emailHtml, 65000)}
                                                    </Flex>
                                                </>
                                            )}
                                        </FormControl>
                                    )}
                                </VStack>
                            </Box>
                        </Box>

                        <Box>
                            <Box position="sticky" top={4}>
                                <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={3}>Preview</Text>
                                <EmailPreview
                                    subject={props.emailSubject}
                                    body={emailCodeMode ? emailCodeInput : props.emailHtml}
                                    emailType={emailCodeMode ? 'html' : props.emailType}
                                />
                            </Box>
                        </Box>
                    </Grid>
                )}
            </VStack>
        ),
    };

    return (
        <Box className='box-style-card' bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <Box>
                <Tabs variant="enclosed" index={props.tabIndex} onChange={props.setTabIndex}>
                    <TabList bg="#f1f5f9" p="1" borderRadius="full" display="flex" justifyContent="space-between">
                        {props.availableChannels.map((channel) => {
                            const channelName = channel.name === "Push Notification" ? "Push Notification" :
                                channel.name === "In-App Notification" ? "Inbox" :
                                    channel.name === "Voice (IVR)" ? "Voice" :
                                        channel.name === "E-Mail" ? "Email" : channel.name;

                            return (
                                <Tab
                                    key={channel.name}
                                    position="relative"
                                    flex="1"
                                    _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }}
                                    borderRadius="full"
                                    fontSize="sm"
                                >
                                    <HStack spacing={2}>
                                        <Icon as={channel.icon} />
                                        <Text display={{ base: 'none', sm: 'inline' }}>{channelName}</Text>
                                    </HStack>

                                    {props.selectedChannels.includes(channel.name) && (
                                        <Box
                                            position="absolute"
                                            top={2}
                                            right={3}
                                            w={4}
                                            h={4}
                                            bg={channel.color}
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <FiCheckCircle size={10} color="white" />
                                        </Box>
                                    )}
                                </Tab>
                            );
                        })}
                    </TabList>

                    <TabPanels>
                        {props.availableChannels.map((channel) => (
                            <TabPanel key={channel.name}>
                                {channelPanels[channel.name]?.()}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            </Box>

            {/* LOCAL ONLY — AI Agents: Voice Onboarding modal (FEAT-0008).
                Opened by the "Setup Brand Voice for AI" button on any
                channel tab when no profile exists yet. After save the
                button flips to "Generate Templates (AI)". If the
                operator had a brief queued (pendingGenerate), we
                immediately open the generator with it. */}
            <VoiceOnboardingModal
                isOpen={voiceOnboardingOpen}
                onClose={() => setVoiceOnboardingOpen(false)}
                onSaved={(profile) => {
                    setVoiceProfile(profile);
                    setVoiceOnboardingOpen(false);
                    if (pendingGenerate) {
                        openCopyFor(pendingGenerate.channel, pendingGenerate.text, pendingGenerate.setter);
                        setPendingGenerate(null);
                    }
                }}
            />

            {/* LOCAL ONLY — AI Agents: Copy Suggester modal (mounted once,
                receives the channel/text/setter via openCopyFor). */}
            {aiCopyOpen.channel && (
                <CopySuggesterModal
                    isOpen={!!aiCopyOpen.channel}
                    onClose={() => setAiCopyOpen({ channel: null, text: '', setter: null })}
                    defaults={{
                        brief: aiCopyOpen.text || "",
                        channel: aiCopyOpen.channel,
                        variables: [],
                    }}
                    openerChannel={aiCopyOpen.channel}
                    onUseVariant={(text) => {
                        if (typeof aiCopyOpen.setter === "function") aiCopyOpen.setter(text);
                        setAiCopyOpen({ channel: null, text: '', setter: null });
                    }}
                />
            )}

            {/* LOCAL ONLY — AI Agents: Compliance Reviewer modal.
                Pre-fills the category from the form's Template Type so
                the operator doesn't pick it twice. Compliance agent
                applies category-specific rules. (FEAT-0011.) */}
            {aiComplianceOpen.channel && (
                <ComplianceReviewerModal
                    isOpen={!!aiComplianceOpen.channel}
                    onClose={() => setAiComplianceOpen({ channel: null, text: '' })}
                    defaults={{
                        template_text: aiComplianceOpen.text || "",
                        channel: aiComplianceOpen.channel,
                        category: resolveComplianceCategory(props.templateType, props.templateTypes || []) || undefined,
                    }}
                />
            )}
        </Box>
    );
}